<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Label from "@/Components/Settings/Label.vue";
import Button from "@/Components/Button.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import Heading from "@/Components/Settings/Heading.vue";
import Modal from "@/Components/Settings/Modal.vue";

import { Head, useForm, Link } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref } from "vue";

const page_name = "Behandler/innen";

const props = defineProps({
  practitioners: Array,
  amount_pracititoners: Number,
  max_pracititoners: Number,
});

const modalData = ref({
  show: false,
  id: Number,
});

const canEdit = Inertia.page.props.auth.can["edit settings"];
const canDelete = Inertia.page.props.auth.can["delete settings"];

function showDeleteModal(id) {
  if (props.practitioners.length < 2) {
    alert("Es muss mindestens ein/e Behandler/in existieren.");
    return;
  }
  modalData.value.id = id;
  modalData.value.show = true;
}

function deleteUser(id) {
  Inertia.delete(
    `/practitioners/${id}`,
    {},
    { preserveState: true, replace: true }
  );
  modalData.value.show = false;
}

const form = useForm({});
</script>

<template>
  <SettingsLayout>
    <Modal :show="modalData.show" @close="modalData.show = false">
      <template #title>
        <div>Warnung</div>
      </template>
      <template #message>
        <!-- {{ modalData.message }} -->
        <p>
          Die Daten des Behandlers sowie zugewiesene Termine werden
          unwiderruflich gelöscht.
        </p>
      </template>
      <template #actions>
        <div class="">
          <div class="flex gap-5 justify-center mt-8">
            <Button
              class="mt-auto"
              @click="
                () => {
                  modalData.show = false;
                }
              "
              >Abbrechen</Button
            >

            <Button
              type="button"
              class="mt-auto"
              @click="deleteUser(modalData.id)"
              >Löschen</Button
            >
          </div>
        </div>
      </template>
    </Modal>

    <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <HeaderLink href="/" type="close">Schließen</HeaderLink>
    </template>
    <Heading class="inline-block" title="Behandler/innen" />
    <div class="inline-block ml-5">
      {{ practitioners.length }} / {{ max_pracititoners }}
    </div>

    <p class="max-w-4xl text-sm text-oteoText mb-9">
      Sie können hier einen neue/n Behandler/in anlegen mit Klick auf den Button
      "neuen Behandler anlegen". Sie geben Namen, Titel und Tätigkeit an, sowie
      das Kürzel (max.3 Zeichen) mit denen der/die Behandler/in im Terminplaner
      erscheinen soll.
      <br />
      <br />
      Wenn der/die Behandler/in auch mit Ihrem Terminplaner oteo arbeiten soll,
      müssen Sie ihn in den Grundeinstellungen über die Nutzerverwaltung
      einladen. Ihr/e Behandler kann sich so einen eigenen Account für oteo
      anlegen und Termine sehen und bearbeiten.
      <br />
      <br />
      Bitte beachten Sie, dass Sie in Ihrem Terminplaner oteo maximal 4
      Behandler/innen anlegen können. Jede/n weitere/n Behandler/in müssen Sie
      separat dazu buchen.
    </p>

    <Button
      v-if="canEdit"
      href="/einstellungen/behandlerinnen/erstellen"
      class="mb-9"
    >
      <span v-if="amount_pracititoners < max_pracititoners">
        Neuen Behandler anlegen</span
      >
      <span v-if="amount_pracititoners >= max_pracititoners">
        Neuen Behandler anlegen (kostenpflichtig)</span
      >
    </Button>

    <div class="flex flex-col">
      <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="py-2 inline-block min-w-full sm:px-6 lg:px-8">
          <div class="overflow-auto max-h-[275px]">
            <table class="min-w-full">
              <thead class="bg-white border-b">
                <tr>
                  <th
                    scope="col"
                    class="
                      text-sm
                      font-medium
                      text-gray-900
                      px-6
                      py-4
                      text-left
                      border-x-2
                    "
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    class="
                      text-sm
                      font-medium
                      text-gray-900
                      px-6
                      py-4
                      text-left
                      border-x-2
                    "
                  >
                    Kürzel
                  </th>
                  <th
                    scope="col"
                    class="
                      text-sm
                      font-medium
                      text-gray-900
                      px-6
                      py-4
                      text-left
                      border-x-2
                    "
                  >
                    Tätigkeit
                  </th>
                  <th
                    scope="col"
                    class="
                      text-sm
                      font-medium
                      text-gray-900
                      px-6
                      py-4
                      text-left
                      border-x-2
                    "
                  >
                    Aktion
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(user, index) in practitioners"
                  :key="user.id"
                  class="border-b"
                  :class="{ 'bg-gray-100': index % 2 === 0 }"
                >
                    <TD class="relative">
                        <div class="selectStatusIcon" :style="`background-color:${ user.color }`"></div>
                        <div class="pl-2">{{ user.fullname }}</div>
                    </TD>
                  <TD>{{ user.username }}</TD>
                  <TD>{{ user.job }}</TD>
                  <TD>
                    <div class="flex items-center text-oteo-300">
                      <Link
                        v-if="canEdit"
                        class="mr-4"
                        :href="`behandlerinnen/${user.id}/bearbeiten/`"
                      >
                        <i class="las la-angle-right"></i>bearbeiten
                      </Link>
                      <div
                        v-if="canDelete"
                        class="cursor-pointer"
                        @click="showDeleteModal(user.id)"
                      >
                        <i class="las la-times"></i>
                        löschen
                      </div>
                    </div>
                  </TD>
                </tr>

                <tr>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                  <TD></TD>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>

